import type { InventoryVehicle } from '@carvana/merch-contracts';
import { InventoryTypeId } from '@carvana/merch-contracts/InventoryType';
import {
  useEffectWithController,
  useLoadableState,
  usePromise
} from '@carvana/merch-react-hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import SortOption from '~/constants/common/SortOption';
import { blobCdnUrl, vexCdnUrl } from '~/constants/environment';
import DeviceContext, {
  useHasAccurateDeviceType,
  useIsMobile
} from '~/context/common/DeviceContext';
import StaticContext from '~/context/common/StaticContext';
import UserContext from '~/context/common/UserContext';
import type { AppliedFilters, RequestedFeature } from '~/contracts/common';
import fetchInventory from '~/services/common/fetchInventory';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

const requestedFeatures: RequestedFeature[] = [
  'Personalization',
  'LocationBasedPrefiltering',
  'EarliestAcquisitionBoosting'
];

export default function useGetVehicles(
  filters: Partial<AppliedFilters>,
  fetchTrigger: boolean,
  errorLocation: string
) {
  const { browserName } = useContext(DeviceContext);
  const { clientId } = useContext(StaticContext);
  const isMobile = useIsMobile();
  const {
    location: {
      data: { zip = '' }
    }
  } = useContext(UserContext);
  const {
    data: inventory,
    error,
    isLoaded,
    setData,
    setError,
    setLoading
  } = useLoadableState<InventoryVehicle[]>([]);
  const mounted = usePromise();
  const hasAccurateDeviceType = useHasAccurateDeviceType();
  const [shouldFetch, setShouldFetch] = useState(fetchTrigger);
  const isMobileRef = useRef(isMobile);

  useEffect(() => {
    if (fetchTrigger) {
      setShouldFetch(fetchTrigger);
    }
  }, [fetchTrigger, setLoading]);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffectWithController(
    controller => {
      if (!hasAccurateDeviceType) return;
      if (!zip || !shouldFetch) {
        setData([]);

        return;
      }

      async function fetchVehicles() {
        setLoading();

        const { data } = await fetchInventory(
          {
            analyticsData: {
              browser: browserName ?? '',
              clientId,
              deviceName: '',
              isMobileDevice: isMobileRef.current,
              referrer: document.referrer
            },
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            filters: {
              ...filters
            },
            pagination: { page: 1, pageSize: 12 },
            requestedFeatures,
            sortBy: SortOption.MostPopular,
            zip5: zip
          },
          controller
        );

        return data.inventory.vehicles;
      }

      mounted(fetchVehicles())
        .then(vehicles => {
          setData(
            vehicles.map<InventoryVehicle>(vehicle => ({
              ...vehicle,
              imageUrl:
                vehicle.imageUrl == null ||
                vehicle.vehicleInventoryType ===
                  InventoryTypeId.DealerMarketplace
                  ? `${vexCdnUrl}/${vehicle.stockNumber}/hero.jpg`
                  : `${blobCdnUrl}${vehicle.imageUrl}`
            }))
          );
        })
        .catch(
          generateErrorLogger(
            errorLocation,
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      browserName,
      clientId,
      errorLocation,
      filters,
      hasAccurateDeviceType,
      mounted,
      setData,
      setError,
      setLoading,
      shouldFetch,
      zip
    ]
  );

  return {
    error,
    isLoaded,
    vehicles: inventory
  };
}
